import axios from 'axios';
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Square, Video, Upload, Trash2, Share2, Copy, Clock, Users, Camera, CheckCircle, AlertCircle, RotateCcw, FileVideo } from 'lucide-react';
import { API_BASE_URL } from '../../lib/constants';

function useQuery() {
  const params = new URLSearchParams(window.location.search);
  return {
    get: (key) => params.get(key)
  };
}



// Detect Safari for MIME fallback
function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
}

// Detect iOS
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

// Convert bytes to MB
function bytesToMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

const VideoRecorderPage = () => {
  const query = useQuery();
  const formName = query.get('form') || 'test-form';
  const uhid_no = query.get('uhid_no') || 'test-uhid';

  const [activeTab, setActiveTab] = useState('record');
  const [recording, setRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [videoURL, setVideoURL] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [sharedBy, setSharedBy] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [videosList, setVideosList] = useState([]);
  const [shareLink, setShareLink] = useState(null);
  const [expiresHours, setExpiresHours] = useState(24);
  const [maxUses, setMaxUses] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentCamera, setCurrentCamera] = useState('user'); // 'user' for front, 'environment' for back
  const [availableCameras, setAvailableCameras] = useState([]);
  const [maxFileSizeMB] = useState(200); // 100MB limit
  const [maxRecordingMinutes] = useState(5);  // 5 minutes limit

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const MAX_RECORDING_DURATION = 5 * 60;// 5 minutes in seconds
  const [recordDisabled, setRecordDisabled] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Get available cameras
  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(cameras);
      } catch (err) {
        console.log('Could not get cameras:', err);
      }
    };
    getCameras();
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      if (!uhid_no || !formName) return;
      try {
        const res = await axios.get(`${API_BASE_URL}/videos`, {
          params: { uhid_no, form_name: formName }
        });
        setVideosList(res.data.videos || []);
      } catch (err) {
        setMessage('Error fetching videos: ' + (err.response?.data?.error || err.message));
      }
    };
    fetchVideos();
  }, [uhid_no, formName]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (videoURL) URL.revokeObjectURL(videoURL);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [videoURL]);

  useEffect(() => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      setMessage('❌ Your browser does not support camera/mic access. Try using Chrome, Firefox, or Safari over HTTPS.');
    }
  }, []);

 const startRecordingTimer = () => {
  setRecordingTime(0);
  recordingIntervalRef.current = setInterval(() => {
    setRecordingTime(prev => {
      const newTime = prev + 1;
      if (newTime >= 5 * 60) {
        stopRecording();
        setAlertMessage('⚠️ Recording stopped: maximum duration of 5 minutes reached.');
      }
      return newTime;
    });
  }, 1000);
};


  const stopRecordingTimer = () => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const switchCamera = async () => {
    if (recording) {
      setMessage('⚠️ Cannot switch camera while recording. Stop recording first.');
      return;
    }

    const newCamera = currentCamera === 'user' ? 'environment' : 'user';
    setCurrentCamera(newCamera);
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: newCamera }, 
        audio: true 
      });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }
      setMessage(`📱 Switched to ${newCamera === 'user' ? 'front' : 'back'} camera`);
    } catch (err) {
      setMessage('❌ Could not switch camera: ' + err.message);
      setCurrentCamera(currentCamera); // Revert on error
    }
  };

const startRecording = async () => {
  setMessage('');
  setAlertMessage('');
  setRecording(true);
  setRecordingTime(0);

  try {
    const constraints = {
      video: {
        facingMode: currentCamera,
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: true
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = stream;
    }

    recordedChunksRef.current = [];

    let mimeType = 'video/webm;codecs=vp9';
    if (isIOS() || isSafari()) {
      if (MediaRecorder.isTypeSupported('video/mp4;codecs=h264')) {
        mimeType = 'video/mp4;codecs=h264';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
      } else {
        mimeType = 'video/webm';
      }
    }

    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = event => {
    console.log("Chunk received, size:", event.data.size);
    if (event.data.size > 0) {
      recordedChunksRef.current.push(event.data);
    }
  };

    recorder.onstop = () => {
    const blob = new Blob(recordedChunksRef.current, { type: mimeType });

    // 🔍 Debugging logs — paste these here
    console.log("Recorded chunks:", recordedChunksRef.current.length);
    console.log("Final blob size (bytes):", blob.size);
    console.log("Final blob:", blob);

    const url = URL.createObjectURL(blob);
    setVideoBlob(blob);
    setVideoURL(url);
    setRecordDisabled(true);
  };


    recorder.start();
    startRecordingTimer(); // ✅ Only this needed

  } catch (err) {
    console.error('Error accessing media devices:', err);
    setAlertMessage('❌ Failed to access camera/microphone.');
  }
};

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopRecordingTimer();
      if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
      streamRef.current?.getTracks().forEach(track => (track.enabled = false));
      setMessage('⏸️ Recording paused');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startRecordingTimer();
      streamRef.current?.getTracks().forEach(track => (track.enabled = true));
      if (videoPreviewRef.current) videoPreviewRef.current.srcObject = streamRef.current;
      setMessage('▶️ Recording resumed');
    }
  };

  const stopRecording = () => {
  if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
    mediaRecorderRef.current.stop(); // ✅ Finalizes the video blob
  }

  if (streamRef.current) {
    streamRef.current.getTracks().forEach(track => track.stop()); // ✅ Releases camera/mic
  }

  setRecording(false);
  stopRecordingTimer(); // Optional if you're tracking duration
};


 const deleteVideo = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/upload/${filename}/${uhid_no}`);
      setVideosList(videosList.filter(video => video.filename !== filename));
      setMessage('Video deleted successfully.');
    } catch (error) {
      setMessage('Failed to delete video: ' + (error.response?.data?.error || error.message));
    }
  };

  const uploadVideo = async () => {
  if (!videoBlob) return setMessage('❌ No video to upload');
  if (!uhid_no || !formName) return setMessage('❌ Missing uhid_no or formName');

  const sizeMB = bytesToMB(videoBlob.size);
  if (sizeMB > maxFileSizeMB) {
    setMessage(`❌ Video file too large (${sizeMB}MB). Max allowed: ${maxFileSizeMB}MB`);
    return;
  }

  setUploading(true);
  setMessage('📤 Uploading video... Please wait.');

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const extension = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
  const filename = `${uhid_no}_video_${timestamp}.${extension}`;

  const formData = new FormData();
  formData.append('video', videoBlob, filename);
  formData.append('uhid_no', uhid_no);
  formData.append('form_name', formName);
  formData.append('shared_by', sharedBy || 'anonymous');

  try {
    await axios.post(`${API_BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    setMessage(`✅ Upload successful! Filename: ${filename} (${sizeMB}MB)`);
    setVideoBlob(null);
    setVideoURL(null);
    setSharedBy('');
    setRecordingTime(0);
    setRecordDisabled(false);

    // Refresh video list
    const res = await axios.get(`${API_BASE_URL}/videos`, {
      params: { uhid_no, form_name: formName }
    });
    setVideosList(res.data.videos || []);
  } catch (err) {
    if (err.response?.status === 413) {
      setMessage(`❌ Upload failed: File too large (current: ${sizeMB}MB).`);
    } else {
      setMessage('❌ Upload failed: ' + (err.response?.data?.error || err.message));
    }
  } finally {
    setUploading(false);
  }
};


  const createShareLink = async () => {
    if (!creatorName.trim()) {
      setMessage('❌ Please enter your name before creating share link.');
      return;
    }

    try {
      // Simulated API call - replace with actual API
      const shareUrl = `${window.location.origin}/share/${uhid_no}/${formName}?expires=${expiresHours}&max_uses=${maxUses}&created_by=${encodeURIComponent(creatorName)}`;
      setShareLink(shareUrl);
      setMessage('✅ Share link created successfully!');
    } catch (err) {
      setMessage('❌ Error creating share link: ' + err.message);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        setMessage('✅ Link copied to clipboard!');
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setMessage('✅ Link copied to clipboard!');
      }
    }
  };

  if (!uhid_no || !formName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-red-200 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-600 mb-2">Missing Parameters</h2>
          <p className="text-gray-600">Required parameters <strong>uhid_no</strong> or <strong>form</strong> are missing</p>
        </div>
      </div>
    );
  }

  const MessageAlert = ({ message, type = 'info' }) => {
    if (!message) return null;
    
    const colors = {
      info: 'bg-teal-50 border-teal-200 text-teal-800',
      success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      error: 'bg-red-50 border-red-200 text-red-800'
    };

    const icons = {
      info: <AlertCircle className="w-5 h-5" />,
      success: <CheckCircle className="w-5 h-5" />,
      error: <AlertCircle className="w-5 h-5" />
    };

    const getMessageType = (msg) => {
      if (msg.includes('✅') || msg.includes('successful') || msg.includes('Copied')) return 'success';
      if (msg.includes('❌') || msg.includes('Error') || msg.includes('Failed')) return 'error';
      return 'info';
    };

    const messageType = getMessageType(message);

    return (
      <div className={`border rounded-lg p-4 flex items-start gap-3 ${colors[messageType]} mb-4`}>
        {icons[messageType]}
        <p className="font-medium text-sm leading-relaxed">{message}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-teal-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Video className="w-6 h-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">Video Recorder</h1>
              <p className="text-gray-600">
                Form: <span className="font-semibold text-teal-600">{formName}</span>
              </p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg transition-colors"
              aria-label="Go Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
            <p className="text-sm text-teal-700">
              <span className="font-medium">UHID No:</span> <span className="font-mono font-semibold">{uhid_no}</span>
            </p>
          </div>
        </div>

        {/* Message Alert */}
        <MessageAlert message={message} />

        {/* File Size & Time Limits Info */}
        <div className="bg-yellow-50 rounded-xl p-4 mb-6 border border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <h3 className="font-semibold text-yellow-800">Recording Limits</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-yellow-700">
            <div>📁 <strong>Max file size:</strong> {maxFileSizeMB}MB</div>
            <div>⏱️ <strong>Max recording time:</strong> {maxRecordingMinutes} minutes</div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6 border border-teal-100">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { key: 'record', label: 'Record Video', icon: Camera },
              { key: 'shared', label: 'Videos', icon: Video },
              { key: 'share', label: 'Share Link', icon: Share2 }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 min-w-max flex items-center justify-center gap-2 px-6 py-4 font-medium transition-all duration-200 ${
                  activeTab === key
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-t-xl'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Record Tab */}
            {activeTab === 'record' && (
              <div className="space-y-6">
                {/* Camera Controls */}
                <div className="flex flex-wrap gap-3 justify-center">
                  {availableCameras.length > 1 && (
                    <button
                      onClick={switchCamera}
                      disabled={recording}
                      className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg font-medium shadow-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <RotateCcw className="w-4 h-4" />
                      {currentCamera === 'user' ? 'Switch to Back' : 'Switch to Front'}
                    </button>
                  )}
                  
                  {recording && (
                    <div className="bg-red-100 text-red-800 px-4 py-2 rounded-lg font-mono font-semibold border border-red-200">
                      🔴 {formatTime(recordingTime)} / {formatTime(maxRecordingMinutes * 60)}
                    </div>
                  )}
                </div>

              {alertMessage && (
      <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-lg px-4 py-3 mb-4 text-center">
        {alertMessage}
      </div>
    )}

                {!videoURL && (
                  <div className="relative">
                    <video 
                      ref={videoPreviewRef} 
                      autoPlay 
                      playsInline 
                      muted 
                      className="w-full aspect-video bg-gray-900 rounded-xl shadow-lg object-cover"
                    />
                    {!recording && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-xl">
                        <div className="text-center text-white">
                          <Camera className="w-16 h-16 mx-auto mb-4 opacity-60" />
                          <p className="text-lg font-medium">Ready to Record</p>
                          <p className="text-sm opacity-80">
                            Using {currentCamera === 'user' ? 'Front' : 'Back'} Camera
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!recording && !videoURL && (
                  <div className="text-center">
                    <button 
                      onClick={startRecording}
                      disabled={recording || recordDisabled}
                      className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-3 mx-auto"
                    >
                      <Camera className="w-6 h-6" />
                      Start Recording
                    </button>
                  </div>
                )}

                {recording && (
                  <div className="flex flex-wrap justify-center gap-4">
                    {!isPaused ? (
                      <button 
                        onClick={pauseRecording}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg flex items-center gap-2 transition-all duration-200"
                      >
                        <Pause className="w-5 h-5" />
                        Pause
                      </button>
                    ) : (
                      <button 
                        onClick={resumeRecording}
                        className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg flex items-center gap-2 transition-all duration-200"
                      >
                        <Play className="w-5 h-5" />
                        Resume
                      </button>
                    )}
                    <button 
                      onClick={stopRecording}
                      className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <Square className="w-5 h-5" />
                      Stop Recording
                    </button>
                  </div>
                )}

                {videoURL && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-6 h-6 text-emerald-500" />
                        Preview Your Recording
                      </h3>
                      <video 
                        src={videoURL} 
                        controls 
                        playsInline 
                        className="w-full aspect-video rounded-xl shadow-lg"
                        preload="metadata"
                      />
                      <div className="mt-2 text-sm text-gray-600 flex items-center justify-between">
                        <span>📁 Size: {videoBlob ? bytesToMB(videoBlob.size) : '0'}MB</span>
                        <span>⏱️ Duration: {formatTime(recordingTime)}</span>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Shared By (Optional)
                      </label>
                      <input 
                        type="text"
                        value={sharedBy} 
                        onChange={e => setSharedBy(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <button 
                        onClick={uploadVideo} 
                        disabled={uploading || (videoBlob && bytesToMB(videoBlob.size) > maxFileSizeMB)}
                        className="flex-1 min-w-max bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2 transition-all duration-200"
                      >
                        <Upload className="w-5 h-5" />
                        {uploading ? 'Uploading...' : 'Upload Video'}
                      </button>
                      <button 
                        onClick={() => { 
                          if (videoURL) URL.revokeObjectURL(videoURL);
                          setVideoBlob(null); 
                          setVideoURL(null); 
                          setRecordingTime(0);
                          setRecordDisabled(false); // Allow recording again
                          setAlertMessage('');
                        }}
                        className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold shadow-lg transition-all duration-200"
                      >
                        Record Again
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

               {/* Shared Videos Tab */}
            {activeTab === 'shared' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Video className="w-6 h-6 text-teal-600" />
                  Uploaded Videos
                </h3>
                
                {videosList.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No videos uploaded yet</p>
                    <p className="text-gray-400">Start by recording your first video</p>
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {videosList.map(video => (
                      <div key={video.id} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                        <video 
                          src={video.url} 
                          controls 
                          playsInline 
                          preload="metadata"
                          className="w-full aspect-video"
                        />
                        Your browser does not support the video tag.
                        <div className="p-6">
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-600">Filename</p>
                              <p className="font-mono text-sm font-medium">{video.filename}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Shared By</p>
                              <p className="font-medium">{video.shared_by}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">File Size</p>
                              <p className="font-medium">{video.file_size_mb} MB</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Uploaded</p>
                              <p className="font-medium">{new Date(video.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                          <button 
                            onClick={() => deleteVideo(video.filename)}
                            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg flex items-center gap-2 transition-all duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Video
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Share Link Tab */}
            {activeTab === 'share' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Share2 className="w-6 h-6 text-teal-600" />
                  Create Share Link
                </h3>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Expires in (hours)
                    </label>
                    <input 
                      type="number" 
                      value={expiresHours} 
                      onChange={e => setExpiresHours(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Max Uses (optional)
                    </label>
                    <input 
                      type="number" 
                      value={maxUses} 
                      onChange={e => setMaxUses(e.target.value)}
                      placeholder="Unlimited"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input 
                      type="text"
                      value={creatorName} 
                      onChange={e => setCreatorName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button 
                    onClick={createShareLink}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-3 mx-auto"
                  >
                    <Share2 className="w-6 h-6" />
                    Generate Share Link
                  </button>
                </div>

                {shareLink && (
                  <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
                    <h4 className="font-semibold text-teal-800 mb-4">Your Share Link is Ready!</h4>
                    <div className="bg-white rounded-lg p-4 border border-teal-200 mb-4">
                      <a 
                        href={shareLink} 
                        target="_blank" 
                        rel="noreferrer"
                        className="text-teal-600 hover:text-teal-800 break-all font-mono text-sm"
                      >
                        {shareLink}
                      </a>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button 
                        onClick={copyToClipboard}
                        className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg flex items-center gap-2 transition-all duration-200"
                      >
                        <Copy className="w-4 h-4" />
                        Copy Link
                      </button>
                      <a 
                        href={`https://wa.me/?text=${encodeURIComponent(shareLink)}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg transition-all duration-200 inline-flex items-center gap-2"
                      >
                        Share via WhatsApp
                      </a>
                      <a 
                        href={`mailto:?subject=Video%20Share&body=${encodeURIComponent(shareLink)}`}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg transition-all duration-200 inline-flex items-center gap-2"
                      >
                        Share via Email
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="mb-6">
            <MessageAlert 
              message={message} 
              type={message.includes('successful') || message.includes('Copied') ? 'success' : message.includes('Error') || message.includes('Failed') ? 'error' : 'info'} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRecorderPage;
